import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Bookmark, BookmarkCheck, Flag } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { mediaUrl } from "@/components/ott/VideoCard";
import { ReportDialog } from "@/components/ott/ReportDialog";

export default function OttWatch() {
  const { id } = useParams();
  const { user } = useAuth();
  const [v, setV] = useState(null);
  const [err, setErr] = useState("");
  const [reporting, setReporting] = useState(false);
  const [playErr, setPlayErr] = useState(false);
  // Persist the exact position when the tab is hidden or the page is left.
  useEffect(() => {
    const onHide = () => { if (document.visibilityState === "hidden") save(true); };
    document.addEventListener("visibilitychange", onHide);
    return () => { document.removeEventListener("visibilitychange", onHide); save(true); };
  }, [id, user]); // eslint-disable-line react-hooks/exhaustive-deps
  const ref = useRef(null);
  const lastSaved = useRef(0);
  const posRef = useRef(0);  // last known playback time — survives the <video> ref being cleared on unmount
  useEffect(() => {
    api.get(`/ott/videos/${id}`).then((r) => setV(r.data)).catch((e) => setErr(e.response?.data?.detail || "Video not available."));
  }, [id]);
  const save = (force = false) => {
    const t = posRef.current;
    if (!user || t < 1 || (force ? t === lastSaved.current : Math.abs(t - lastSaved.current) < 3)) return;
    lastSaved.current = t;
    api.put(`/ott/progress/${id}`, { position_s: t }).catch(() => {});
  };
  const toggle = () => {
    if (!user) { toast.info("Sign in to use your watchlist"); return; }
    api.post(`/ott/watchlist/${id}`).then((r) => setV({ ...v, in_watchlist: r.data.in_watchlist })).catch(() => toast.error("Could not update watchlist"));
  };
  if (err) return <div className="max-w-xl mx-auto py-16 text-center text-stone-600" data-testid="ott-watch-error">{err}</div>;
  if (!v) return <div className="py-16 text-center text-stone-400">Loading…</div>;
  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="ott-watch-page">
      <div className="aspect-video bg-black rounded-2xl overflow-hidden">
        {v.kind === "upload"
          ? <video ref={ref} src={mediaUrl(v.stream_url)} controls playsInline className="w-full h-full" data-testid="ott-player"
                   onLoadedMetadata={() => { if (v.position_s >= 3 && ref.current) ref.current.currentTime = v.position_s; }}
                   onTimeUpdate={() => { posRef.current = ref.current?.currentTime || posRef.current; save(); }}
                   onPause={() => { posRef.current = ref.current?.currentTime || posRef.current; save(true); }}
                   onError={() => setPlayErr(true)} />
          : v.external?.mode === "link_out"
            ? <a href={v.external?.url} target="_blank" rel="noopener noreferrer" className="w-full h-full grid place-items-center text-white text-sm font-bold" data-testid="ott-link-out">Watch on {v.external?.provider} ↗ (their app or subscription may be required)</a>
            : <iframe src={v.external?.embed_url} title={v.title} className="w-full h-full" allow="encrypted-media; picture-in-picture; fullscreen" data-testid="ott-embed" />}
      </div>
      {playErr && <div className="mt-2 text-xs font-bold text-red-700 bg-red-50 rounded-xl px-3 py-2" data-testid="ott-play-error">This browser can't play this video format ({v.kind === "upload" ? "H.264/MP4" : "embed"}). Try Chrome, Edge or Safari.</div>}
      {v.status !== "published" && <div className="mt-2 text-xs font-bold text-amber-800 bg-amber-50 rounded-xl px-3 py-2" data-testid="ott-status-banner">Status: {v.status.replace("_", " ")} — only you and moderators can see this.</div>}
      <h1 className="heading text-2xl font-black text-stone-900 mt-3">{v.title}</h1>
      <div className="flex flex-wrap items-center justify-between gap-2 mt-1">
        <Link to={`/ott/creator/${v.creator_id}`} className="text-sm font-bold text-stone-700 hover:underline" data-testid="ott-creator-link">{v.creator_name}</Link>
        <div className="flex gap-2">
          <button onClick={toggle} className="inline-flex items-center gap-1 text-sm font-bold border border-stone-200 rounded-full px-3 py-1.5" data-testid="ott-watchlist-btn">
            {v.in_watchlist ? <BookmarkCheck size={14} /> : <Bookmark size={14} />} {v.in_watchlist ? "In watchlist" : "Watchlist"}
          </button>
          {user && <button onClick={() => setReporting(true)} className="inline-flex items-center gap-1 text-sm font-bold text-stone-500 border border-stone-200 rounded-full px-3 py-1.5" data-testid="ott-report-btn"><Flag size={14} /> Report</button>}
        </div>
      </div>
      <div className="text-xs text-stone-500 mt-1">{v.views} views · {v.category} {v.kind === "link" && `· via ${v.external?.provider}`}</div>
      {v.kind === "link" && v.external?.playable !== "verified" && (
        <div className="text-[11px] text-stone-500 mt-1" data-testid="ott-embed-unverified">
          Playback depends on {v.external?.provider}. If it doesn't load, <a href={v.external?.url} target="_blank" rel="noopener noreferrer" className="underline font-bold" data-testid="ott-open-provider">open it there</a>.
        </div>
      )}
      {v.description && <p className="text-sm text-stone-700 mt-3 whitespace-pre-line">{v.description}</p>}
      {reporting && <ReportDialog videoId={id} onClose={() => setReporting(false)} />}
    </div>
  );
}
